// Attribute allow-list for analytics events. Pure and unit-tested because it is
// the last line of defence: the dialog surface holds respondent PII and the
// invite token is a capability credential, so nothing reaches an event unless
// it is named here.
import { redactSensitive } from '@/lib/error-scrub';

export const ALLOWED_ATTR_KEYS = ['sessionId', 'itemCount'] as const;

export type AllowedAttrKey = (typeof ALLOWED_ATTR_KEYS)[number];

export type TrackAttrs = Partial<Record<AllowedAttrKey, string | number>>;

const MAX_STRING_LENGTH = 128;

// Iterates the allow-list rather than the input, so an unexpected key cannot
// pass by being overlooked — it has to be added here deliberately, which makes
// the tests the place that argument gets had.
export function sanitiseAttrs(
  attrs: Record<string, unknown> | undefined,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (!attrs) return out;

  for (const key of ALLOWED_ATTR_KEYS) {
    const value = attrs[key];
    if (typeof value === 'number') {
      // NaN and Infinity serialise to null in JSON, so drop them rather than
      // emit an attribute that reads as missing downstream.
      if (Number.isFinite(value)) out[key] = value;
    } else if (typeof value === 'string') {
      out[key] = redactSensitive(value).slice(0, MAX_STRING_LENGTH);
    }
    // Anything else — objects, arrays, booleans, null, undefined — is dropped
    // rather than coerced, so a mistake surfaces as a missing attribute instead
    // of leaked content.
  }

  return out;
}
